<template>
  <v-container>
    <!-- Header -->
    <v-row
      class="mb-4"
      align="center"
    >
      <v-col>
        <h2>{{ t.pageTitle }}</h2>
      </v-col>
      <v-col class="text-right d-flex align-center justify-end">
        <v-btn-toggle
          v-model="lang"
          mandatory
          dense
          class="mr-4"
        >
          <v-btn
            x-small
            value="lo"
          >
            ລາວ
          </v-btn>
          <v-btn
            x-small
            value="en"
          >
            EN
          </v-btn>
        </v-btn-toggle>
        <v-btn
          color="orange darken-1"
          dark
          class="mr-2"
          :loading="checkPendingLoading"
          @click="showCheckPendingModal = true"
        >
          <v-icon left>
            mdi-qrcode-scan
          </v-icon>
          {{ t.checkPendingQr }}
        </v-btn>
        <v-btn
          color="#49A3DA"
          dark
          @click="showRunModal = true"
        >
          <v-icon left>
            mdi-play-circle
          </v-icon>
          {{ t.runEod }}
        </v-btn>
        <v-btn
          v-if="$role('super_admin')"
          icon
          class="ml-2"
          @click="openPermissions()"
        >
          <v-icon>mdi-cog</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Batch Cards -->
    <!-- History Button -->
    <v-row class="mb-2">
      <v-col>
        <v-btn
          small
          outlined
          color="grey darken-1"
          @click="$router.push('/eod/ldb-reconciliation/history')"
        >
          <v-icon
            small
            left
          >
            mdi-history
          </v-icon>
          {{ t.viewHistory }}
        </v-btn>
      </v-col>
    </v-row>

    <div
      v-for="batch in openBatches"
      :key="batch.batch_id"
      class="mb-6"
    >
      <v-card
        class="batch-card"
        elevation="2"
      >
        <!-- Card Header -->
        <v-card-title class="batch-header py-3">
          <v-row
            align="center"
            no-gutters
          >
            <v-col>
              <span class="text-h6 font-weight-bold">
                {{ formatDate(batch.date_from) }}<span v-if="batch.date_to && formatDate(batch.date_to) !== formatDate(batch.date_from)"> — {{ formatDate(batch.date_to) }}</span>
              </span>
              <v-chip
                small
                label
                :color="batch.status === 'closed' ? 'green' : batch.status === 'pending_approval' ? 'orange' : 'amber darken-1'"
                dark
                class="ml-3 font-weight-bold"
              >
                {{ batch.status === 'closed' ? t.closed : batch.status === 'pending_approval' ? t.pendingApproval : t.open }}
              </v-chip>
            </v-col>
            <v-col class="text-right text-body-2 grey--text text--darken-1">
              {{ t.runBy }}: <strong>{{ batch.reconciled_by ? batch.reconciled_by.name : '-' }}</strong>
              &nbsp;|&nbsp;
              {{ moment(batch.created_at).format("DD-MM-YYYY HH:mm") }}
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider />

        <v-card-text class="pa-4">
          <!-- Summary Grid -->
          <v-simple-table
            dense
            class="summary-table mb-4"
          >
            <thead>
              <tr>
                <th>{{ t.category }}</th>
                <th class="text-right">
                  {{ t.qty }}
                </th>
                <th class="text-right">
                  {{ t.amount }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="font-weight-bold">
                  {{ t.totalQr }}
                </td>
                <td class="text-right font-weight-bold">
                  {{ batch.total_records }}
                </td>
                <td class="text-right font-weight-bold">
                  {{ fmtLAK(batch.total_amount) }}
                </td>
              </tr>
              <tr class="row-green">
                <td>
                  <v-icon
                    small
                    color="green"
                    class="mr-1"
                  >
                    mdi-check-circle
                  </v-icon>
                  {{ t.matched }}
                </td>
                <td class="text-right">
                  {{ batch.matched_count }}
                </td>
                <td class="text-right">
                  {{ fmtLAK(batch.matched_amount) }}
                </td>
              </tr>
              <tr class="row-red">
                <td>
                  <v-icon
                    small
                    color="red"
                    class="mr-1"
                  >
                    mdi-alert-circle
                  </v-icon>
                  {{ t.missed }}
                </td>
                <td class="text-right">
                  {{ batch.missed_payment_count }}
                </td>
                <td class="text-right">
                  {{ fmtLAK(batch.missed_payment_amount) }}
                </td>
              </tr>
              <tr class="row-orange">
                <td>
                  <v-icon
                    small
                    color="orange"
                    class="mr-1"
                  >
                    mdi-swap-horizontal
                  </v-icon>
                  {{ t.mismatch }}
                </td>
                <td class="text-right">
                  {{ batch.amount_mismatch_count }}
                </td>
                <td class="text-right">
                  {{ fmtLAK(batch.amount_mismatch_amount) }}
                </td>
              </tr>
              <tr class="row-grey">
                <td>
                  <v-icon
                    small
                    color="grey"
                    class="mr-1"
                  >
                    mdi-clock-outline
                  </v-icon>
                  {{ t.expired }}
                </td>
                <td class="text-right">
                  {{ batch.expired_count }}
                </td>
                <td class="text-right">
                  {{ fmtLAK(batch.expired_amount) }}
                </td>
              </tr>
              <tr class="row-amber">
                <td>
                  <v-icon
                    small
                    color="amber darken-3"
                    class="mr-1"
                  >
                    mdi-connection
                  </v-icon>
                  {{ t.errors }}
                </td>
                <td class="text-right">
                  {{ batch.ldb_error_count }}
                </td>
                <td class="text-right">
                  {{ fmtLAK(batch.ldb_error_amount) }}
                </td>
              </tr>
              <tr>
                <td>
                  <v-icon
                    small
                    color="orange"
                    class="mr-1"
                  >
                    mdi-timer-sand
                  </v-icon>
                  {{ t.pending }}
                </td>
                <td class="text-right">
                  {{ batch.pending_count }}
                </td>
                <td class="text-right">
                  —
                </td>
              </tr>
              <tr class="row-green">
                <td>
                  <v-icon
                    small
                    color="green"
                    class="mr-1"
                  >
                    mdi-check-decagram
                  </v-icon>
                  {{ t.approved }}
                </td>
                <td class="text-right">
                  {{ batch.approved_count }}
                </td>
                <td class="text-right">
                  {{ fmtLAK(batch.approved_amount) }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-divider class="mb-4" />

          <!-- Settlement Section -->
          <div class="settlement-section">
            <div class="text-subtitle-1 font-weight-bold mb-3">
              <v-icon
                small
                class="mr-1"
              >
                mdi-bank
              </v-icon>
              {{ t.settlement }}
            </div>

            <!-- Reconciliation Summary Table -->
            <v-simple-table
              dense
              class="summary-table mb-4"
            >
              <thead>
                <tr>
                  <th>{{ t.settlementItem }}</th>
                  <th class="text-right">
                    {{ t.amount }} (LAK)
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr class="row-green">
                  <td>
                    <v-icon
                      small
                      color="blue"
                      class="mr-1"
                    >
                      mdi-bank
                    </v-icon>
                    {{ t.bankStatementTotal }}
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ fmtLAK(calcBankTotal(batch)) }}
                  </td>
                </tr>
                <tr>
                  <td class="pl-8 text-caption grey--text">
                    — BCEL
                  </td>
                  <td class="text-right text-caption grey--text">
                    {{ fmtLAK(batch.bcel_statement_amount || batch.statement_summary && batch.statement_summary.BCEL ? batch.statement_summary.BCEL.total_amount : 0) }}
                  </td>
                </tr>
                <tr>
                  <td class="pl-8 text-caption grey--text">
                    — LDB
                  </td>
                  <td class="text-right text-caption grey--text">
                    {{ fmtLAK(batch.ldb_statement_amount || batch.statement_summary && batch.statement_summary.LDB ? batch.statement_summary.LDB.total_amount : 0) }}
                  </td>
                </tr>
                <tr class="row-green">
                  <td>
                    <v-icon
                      small
                      color="green"
                      class="mr-1"
                    >
                      mdi-check-decagram
                    </v-icon>
                    {{ t.systemConfirmed }}
                  </td>
                  <td class="text-right font-weight-bold">
                    {{ fmtLAK(batch.system_confirmed_amount) }}
                  </td>
                </tr>
                <tr>
                  <td class="pl-8 text-caption grey--text">
                    — {{ t.matched }}
                  </td>
                  <td class="text-right text-caption grey--text">
                    {{ fmtLAK(batch.matched_amount) }}
                  </td>
                </tr>
                <tr>
                  <td class="pl-8 text-caption grey--text">
                    — {{ t.approved }}
                  </td>
                  <td class="text-right text-caption grey--text">
                    {{ fmtLAK(batch.approved_amount) }}
                  </td>
                </tr>
                <tr :class="calcSettlementDiff(batch) == 0 ? 'row-green' : 'row-red'">
                  <td class="font-weight-bold">
                    <v-icon
                      small
                      :color="calcSettlementDiff(batch) == 0 ? 'green' : 'red'"
                      class="mr-1"
                    >
                      {{ calcSettlementDiff(batch) == 0 ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                    </v-icon>
                    {{ t.difference }}
                  </td>
                  <td
                    class="text-right font-weight-bold"
                    :class="calcSettlementDiff(batch) == 0 ? 'green--text' : 'red--text'"
                  >
                    {{ fmtLAK(calcSettlementDiff(batch)) }}
                  </td>
                </tr>
                <tr v-if="Number(batch.pending_count) > 0">
                  <td class="text-caption orange--text">
                    <v-icon
                      x-small
                      color="orange"
                      class="mr-1"
                    >
                      mdi-timer-sand
                    </v-icon>
                    {{ t.pendingReview }}: {{ batch.pending_count }}
                  </td>
                  <td class="text-right text-caption orange--text">
                    {{ t.resolveToClose }}
                  </td>
                </tr>
              </tbody>
            </v-simple-table>

            <!-- Closed info -->
            <div
              v-if="batch.status === 'closed'"
              class="text-caption grey--text mt-2"
            >
              <v-icon
                x-small
                class="mr-1"
              >
                mdi-lock
              </v-icon>
              {{ t.closedBy }}: {{ batch.closed_by ? batch.closed_by.name : '-' }}
              | {{ batch.closed_at ? moment(batch.closed_at).format("DD-MM-YYYY HH:mm") : '' }}
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <!-- Card Actions -->
        <v-card-actions class="pa-4">
          <v-btn
            small
            color="#49A3DA"
            dark
            @click="viewBatch(batch)"
          >
            <v-icon
              small
              left
            >
              mdi-eye
            </v-icon>
            {{ t.viewDetails }}
          </v-btn>
          <v-btn
            small
            color="green"
            dark
            @click="exportBatch(batch.batch_id)"
          >
            <v-icon
              small
              left
            >
              mdi-download
            </v-icon>
            {{ t.export }}
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="batch.status === 'open'"
            small
            icon
            color="red"
            @click="confirmDelete(batch)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>

    <!-- Empty State -->
    <v-card
      v-if="!loading && openBatches.length === 0"
      class="text-center pa-8"
    >
      <v-icon
        size="64"
        color="grey lighten-1"
      >
        mdi-database-off
      </v-icon>
      <p class="text-h6 grey--text mt-4">
        {{ t.noBatches }}
      </p>
    </v-card>

    <!-- Close Confirmation Dialog -->
    <v-dialog
      v-model="showCloseDialog"
      max-width="450"
    >
      <v-card>
        <v-card-title>{{ t.confirmCloseTitle }}</v-card-title>
        <v-card-text>{{ t.confirmCloseMsg }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showCloseDialog = false"
          >
            {{ t.cancel }}
          </v-btn>
          <v-btn
            color="red"
            dark
            :loading="closing !== null"
            @click="doClose()"
          >
            {{ t.closeEod }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      v-model="showDeleteDialog"
      max-width="450"
    >
      <v-card>
        <v-card-title>{{ t.confirmDeleteTitle }}</v-card-title>
        <v-card-text>{{ t.confirmDeleteMsg }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showDeleteDialog = false"
          >
            {{ t.cancel }}
          </v-btn>
          <v-btn
            color="red"
            dark
            :loading="deleting !== null"
            @click="doDelete()"
          >
            {{ t.delete }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Run EOD Modal -->
    <v-dialog
      v-model="showRunModal"
      persistent
      max-width="500"
    >
      <v-card>
        <v-card-title>{{ t.runEodTitle }}</v-card-title>
        <v-card-text>
          <v-menu
            v-model="dateFromMenu"
            :close-on-content-click="true"
            :nudge-right="40"
            min-width="auto"
            offset-y
            transition="scale-transition"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="runForm.date_from"
                dense
                :label="t.reconcileDate"
                outlined
                readonly
                prepend-inner-icon="mdi-calendar"
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker v-model="runForm.date_from" />
          </v-menu>
          <div
            v-if="runLoading"
            class="text-center py-4"
          >
            <v-progress-circular
              indeterminate
              color="primary"
              size="48"
            />
            <p class="mt-3">
              {{ t.processing }}
            </p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="runLoading"
            @click="showRunModal = false"
          >
            {{ t.cancel }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="runLoading"
            :disabled="runLoading"
            @click="runReconciliation()"
          >
            {{ t.runReconciliation }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Permission Management Dialog -->
    <v-dialog
      v-model="showPermDialog"
      max-width="700"
      scrollable
    >
      <v-card>
        <v-card-title class="py-3">
          <v-icon color="#49A3DA" class="mr-2">mdi-shield-account</v-icon>
          {{ t.manageAccess }}
          <v-spacer />
          <v-btn icon @click="showPermDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text style="max-height: 500px;">
          <!-- Roles with Access -->
          <div class="text-subtitle-2 font-weight-bold mb-2 mt-2">
            <v-icon small class="mr-1">mdi-account-group</v-icon>
            {{ t.rolesWithAccess }}
          </div>
          <div
            v-for="role in permData.roles"
            :key="'role-' + role.id"
            class="d-flex align-center mb-1 pa-2"
            style="background: #f5f5f5; border-radius: 4px;"
          >
            <v-chip small label color="indigo" dark>{{ role.name }}</v-chip>
            <v-spacer />
            <v-btn
              x-small
              icon
              color="red"
              :loading="revokingRole === role.name"
              @click="revokeRole(role.name)"
            >
              <v-icon x-small>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-row dense class="mt-2 mb-4">
            <v-col cols="8">
              <v-select
                v-model="selectedRole"
                :items="permData.available_roles"
                item-text="name"
                item-value="name"
                :label="t.selectRole"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="4">
              <v-btn
                small
                color="indigo"
                dark
                :disabled="!selectedRole"
                :loading="assigningRole"
                @click="assignRole()"
              >
                <v-icon small left>mdi-plus</v-icon>
                {{ t.addRole }}
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="mb-3" />

          <!-- Users with Direct Access -->
          <div class="text-subtitle-2 font-weight-bold mb-2">
            <v-icon small class="mr-1">mdi-account</v-icon>
            {{ t.usersDirectAccess }}
          </div>
          <div
            v-for="user in permData.users"
            :key="'user-' + user.id"
            class="d-flex align-center mb-1 pa-2"
            style="background: #f5f5f5; border-radius: 4px;"
          >
            <span class="font-weight-bold mr-2">{{ user.name }}</span>
            <span class="grey--text">{{ user.phone }}</span>
            <v-spacer />
            <v-btn
              x-small
              icon
              color="red"
              :loading="revokingUser === user.id"
              @click="revokeUser(user.id)"
            >
              <v-icon x-small>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-row dense class="mt-2 mb-4">
            <v-col cols="8">
              <v-text-field
                v-model="addUserSearch"
                :label="t.searchUserPhone"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="4">
              <v-btn
                small
                color="indigo"
                dark
                :disabled="!addUserSearch"
                :loading="assigningUser"
                @click="searchAndAssignUser()"
              >
                <v-icon small left>mdi-plus</v-icon>
                {{ t.addUser }}
              </v-btn>
            </v-col>
          </v-row>

          <v-divider class="mb-3" />

          <!-- All Users with Access -->
          <div class="text-subtitle-2 font-weight-bold mb-2">
            <v-icon small class="mr-1">mdi-account-multiple-check</v-icon>
            {{ t.allUsersAccess }}
          </div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>{{ t.userName }}</th>
                <th>{{ t.userPhone }}</th>
                <th>{{ t.accessVia }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="u in permData.all_users"
                :key="'all-' + u.id + u.via"
              >
                <td class="font-weight-bold">{{ u.name }}</td>
                <td>{{ u.phone }}</td>
                <td>
                  <v-chip
                    x-small
                    label
                    :color="u.via === 'role' ? 'indigo' : 'teal'"
                    dark
                  >
                    {{ u.via === 'role' ? u.role_name : t.directPerm }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- Check Pending QR Modal -->
    <v-dialog
      v-model="showCheckPendingModal"
      persistent
      max-width="450"
    >
      <v-card>
        <v-card-title>
          <v-icon
            color="orange darken-1"
            class="mr-2"
          >
            mdi-qrcode-scan
          </v-icon>
          {{ t.checkPendingQrTitle }}
        </v-card-title>
        <v-card-text>
          <p class="mb-3">
            {{ t.checkPendingQrDesc }}
          </p>
          <v-slider
            v-model="checkPendingDays"
            :min="1"
            :max="90"
            :label="t.daysRange"
            thumb-label="always"
            :disabled="checkPendingLoading"
          />
          <div
            v-if="checkPendingLoading"
            class="text-center py-4"
          >
            <v-progress-circular
              indeterminate
              color="orange darken-1"
              size="48"
            />
            <p class="mt-3">
              {{ t.checkPendingProcessing }}
            </p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="checkPendingLoading"
            @click="showCheckPendingModal = false"
          >
            {{ t.cancel }}
          </v-btn>
          <v-btn
            color="orange darken-1"
            dark
            :loading="checkPendingLoading"
            :disabled="checkPendingLoading"
            @click="checkPendingQr()"
          >
            {{ t.checkPendingQr }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import moment from 'moment';

export default {
  name: 'LdbReconciliation',
  title() {
    return 'Vientiane Waste Co-Dev | EOD Reconciliation';
  },
  data() {
    return {
      lang: 'lo',
      translations: {
        lo: {
          pageTitle: 'ກວດສອບ EOD',
          runEod: 'ແລ່ນ EOD',
          runEodTitle: 'ແລ່ນການກວດສອບ EOD',
          reconcileDate: 'ວັນທີກວດສອບ',
          dateFrom: 'ວັນທີເລີ່ມ',
          dateTo: 'ວັນທີສິ້ນສຸດ',
          cancel: 'ຍົກເລີກ',
          runReconciliation: 'ແລ່ນການກວດສອບ',
          processing: 'ກຳລັງປະມວນຜົນ... ອາດໃຊ້ເວລາເຖິງ 90 ວິນາທີ.',
          runBy: 'ແລ່ນໂດຍ',
          category: 'ລາຍການ',
          qty: 'ຈຳນວນ',
          amount: 'ມູນຄ່າ (LAK)',
          totalQr: 'ລວມ QR ທັງໝົດ',
          matched: 'ກົງກັນ',
          missed: 'ຂາດການຊຳລະ',
          mismatch: 'ຈຳນວນບໍ່ກົງ',
          expired: 'ໝົດອາຍຸ',
          errors: 'ຜິດພາດ LDB',
          pending: 'ລໍຖ້າການກວດ',
          approved: 'ອະນຸມັດແລ້ວ',
          settlement: 'ການຕັ້ງຖານ',
          actualReceived: 'ຈຳນວນທີ່ໄດ້ຮັບຕົວຈິງ',
          systemConfirmed: 'ລະບົບຢືນຢັນ',
          difference: 'ຜົນຕ່າງ',
          comment: 'ໝາຍເຫດ',
          save: 'ບັນທຶກ',
          closeEod: 'ປິດ EOD',
          closed: 'ປິດແລ້ວ',
          open: 'ເປີດ',
          closedBy: 'ປິດໂດຍ',
          confirmCloseTitle: 'ຢືນຢັນການປິດ EOD',
          confirmCloseMsg: 'ທ່ານແນ່ໃຈບໍ່? ການປິດຈະລ໋ອກ batch ນີ້ ບໍ່ສາມາດແກ້ໄຂໄດ້.',
          viewDetails: 'ເບິ່ງລາຍລະອຽດ',
          export: 'ສົ່ງອອກ',
          noBatches: 'ບໍ່ມີຂໍ້ມູນການກວດສອບ',
          reconciliationFailed: 'ການກວດສອບລົ້ມເຫຼວ',
          delete: 'ລຶບ',
          confirmDeleteTitle: 'ຢືນຢັນການລຶບ',
          confirmDeleteMsg: 'ລຶບ batch ການກວດສອບນີ້ບໍ? ການກະທຳນີ້ບໍ່ສາມາດຍ້ອນກັບໄດ້.',
          viewHistory: 'ເບິ່ງປະຫວັດ',
          pendingApproval: 'ລໍຖ້າອະນຸມັດ',
          manageAccess: 'ຈັດການສິດເຂົ້າເຖິງ',
          rolesWithAccess: 'ບົດບາດທີ່ມີສິດ',
          usersDirectAccess: 'ຜູ້ໃຊ້ທີ່ມີສິດໂດຍກົງ',
          allUsersAccess: 'ຜູ້ໃຊ້ທັງໝົດທີ່ມີສິດ',
          selectRole: 'ເລືອກບົດບາດ',
          addRole: 'ເພີ່ມບົດບາດ',
          searchUserPhone: 'ຄົ້ນຫາຜູ້ໃຊ້ (ເບີໂທ/ຊື່)',
          addUser: 'ເພີ່ມຜູ້ໃຊ້',
          userName: 'ຊື່',
          userPhone: 'ເບີໂທ',
          accessVia: 'ຜ່ານ',
          directPerm: 'ສິດໂດຍກົງ',
          settlementItem: 'ລາຍການ',
          bankStatementTotal: 'ລວມຈາກໃບແຈ້ງທະນາຄານ',
          pendingReview: 'ລໍຖ້າການກວດ',
          resolveToClose: 'ແກ້ໄຂເພື່ອປິດ EOD',
          checkPendingQr: 'ກວດສອບ QR ຄ້າງ',
          checkPendingQrTitle: 'ກວດສອບການຊຳລະ QR ທີ່ຄ້າງ',
          checkPendingQrDesc: 'ກວດສອບສະຖານະການຊຳລະ QR ທີ່ຍັງຄ້າງຢູ່ກັບ LDB ແລະ ອັບເດດຜົນ.',
          daysRange: 'ຈຳນວນມື້',
          checkPendingProcessing: 'ກຳລັງກວດສອບ QR ຄ້າງ... ອາດໃຊ້ເວລາ 30-60 ວິນາທີ.',
          checkPendingSuccess: '{completed} ລາຍການກູ້ຄືນສຳເລັດ, {still_pending} ຍັງຄ້າງຢູ່',
          checkPendingNone: 'ບໍ່ມີລາຍການໃໝ່ທີ່ສຳເລັດ, {still_pending} ຍັງຄ້າງຢູ່',
        },
        en: {
          pageTitle: 'EOD Reconciliation',
          runEod: 'Run EOD',
          runEodTitle: 'Run EOD Reconciliation',
          reconcileDate: 'Reconciliation Date',
          dateFrom: 'Date From',
          dateTo: 'Date To',
          cancel: 'Cancel',
          runReconciliation: 'Run Reconciliation',
          processing: 'Processing reconciliation... This may take up to 90 seconds.',
          runBy: 'Run By',
          category: 'Category',
          qty: 'Qty',
          amount: 'Amount (LAK)',
          totalQr: 'Total QR',
          matched: 'Matched',
          missed: 'Missed Payment',
          mismatch: 'Amount Mismatch',
          expired: 'Expired',
          errors: 'LDB Errors',
          pending: 'Pending Review',
          approved: 'Approved',
          settlement: 'Settlement',
          actualReceived: 'Actual Received',
          systemConfirmed: 'System Confirmed',
          difference: 'Difference',
          comment: 'Comment',
          save: 'Save',
          closeEod: 'Close EOD',
          closed: 'Closed',
          open: 'Open',
          closedBy: 'Closed by',
          confirmCloseTitle: 'Confirm Close EOD',
          confirmCloseMsg: 'Are you sure? This will lock the batch and cannot be undone.',
          viewDetails: 'View Details',
          export: 'Export',
          noBatches: 'No reconciliation batches found',
          reconciliationFailed: 'Reconciliation failed',
          delete: 'Delete',
          confirmDeleteTitle: 'Delete Batch',
          confirmDeleteMsg: 'Delete this reconciliation batch? This cannot be undone.',
          viewHistory: 'View History',
          pendingApproval: 'Pending Approval',
          manageAccess: 'Manage Access',
          rolesWithAccess: 'Roles with Access',
          usersDirectAccess: 'Users with Direct Access',
          allUsersAccess: 'All Users with Access',
          selectRole: 'Select role',
          addRole: 'Add Role',
          searchUserPhone: 'Search user (phone/name)',
          addUser: 'Add User',
          userName: 'Name',
          userPhone: 'Phone',
          accessVia: 'Via',
          directPerm: 'Direct',
          settlementItem: 'Item',
          bankStatementTotal: 'Bank Statement Total',
          pendingReview: 'Pending Review',
          resolveToClose: 'Resolve to close EOD',
          checkPendingQr: 'Check Pending QR',
          checkPendingQrTitle: 'Check Pending QR Payments',
          checkPendingQrDesc: 'Re-check pending QR payment statuses with LDB and update results.',
          daysRange: 'Days',
          checkPendingProcessing: 'Checking pending QR payments... This may take 30-60 seconds.',
          checkPendingSuccess: '{completed} payments recovered, {still_pending} still pending',
          checkPendingNone: 'No new completions, {still_pending} still pending',
        },
      },
      batches: [],
      settlements: {},
      loading: false,
      saving: null,
      closing: null,
      closingBatch: null,
      showCloseDialog: false,
      deletingBatch: null,
      deleting: null,
      showDeleteDialog: false,
      showRunModal: false,
      showPermDialog: false,
      permData: {
        roles: [], users: [], all_users: [], available_roles: [],
      },
      selectedRole: '',
      addUserSearch: '',
      assigningRole: false,
      revokingRole: null,
      assigningUser: false,
      revokingUser: null,
      showCheckPendingModal: false,
      checkPendingLoading: false,
      checkPendingDays: 14,
      runLoading: false,
      dateFromMenu: false,
      dateToMenu: false,
      runForm: {
        date_from: moment().format('YYYY-MM-DD'),
      },
    };
  },
  computed: {
    t() {
      return this.translations[this.lang];
    },
    openBatches() {
      return this.batches.filter((b) => b.status === 'open');
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    moment,
    formatDate(d) {
      if (!d) return '-';
      return moment(d).format('DD-MM-YYYY');
    },
    fmtLAK(val) {
      if (val === null || val === undefined) return '-';
      return `₭${Intl.NumberFormat().format(Math.round(Number(val)))}`;
    },
    fmtNumber(val) {
      if (val === null || val === undefined || val === '') return '-';
      return Intl.NumberFormat().format(Math.round(Number(val)));
    },
    formatSettlementInput(batch) {
      const s = this.getSettlement(batch);
      if (!s.actual_received_amount && s.actual_received_amount !== 0) return '';
      return Intl.NumberFormat().format(Math.round(Number(s.actual_received_amount)));
    },
    onActualReceivedInput(batch, value) {
      const s = this.getSettlement(batch);
      const raw = value.replace(/[^0-9]/g, '');
      s.actual_received_amount = raw;
    },
    getSettlement(batch) {
      const key = batch.batch_id;
      if (!this.settlements[key]) {
        this.$set(this.settlements, key, {
          actual_received_amount: batch.actual_received_amount || '',
          comment: batch.comment || '',
        });
      }
      return this.settlements[key];
    },
    calcDifference(batch) {
      const s = this.getSettlement(batch);
      if (!s.actual_received_amount && s.actual_received_amount !== 0) return '-';
      const diff = Number(s.actual_received_amount) - Number(batch.system_confirmed_amount || 0);
      return Intl.NumberFormat().format(Math.round(diff));
    },
    diffClass(batch) {
      const s = this.getSettlement(batch);
      if (!s.actual_received_amount && s.actual_received_amount !== 0) return '';
      const diff = Number(s.actual_received_amount) - Number(batch.system_confirmed_amount || 0);
      if (diff === 0) return 'diff-zero';
      return 'diff-nonzero';
    },
    calcBankTotal(batch) {
      const s = this.getSettlement(batch);
      if (s.actual_received_amount) {
        return Number(s.actual_received_amount);
      }
      if (batch.actual_received_amount) {
        return Number(batch.actual_received_amount);
      }
      return 0;
    },
    calcSettlementDiff(batch) {
      const bankTotal = this.calcBankTotal(batch);
      const systemTotal = Number(batch.system_confirmed_amount || 0);
      if (bankTotal === 0) return '-';
      return bankTotal - systemTotal;
    },
    fetchData() {
      this.loading = true;
      this.$store.commit('Loading_State', true);
      this.$axios
        .get('ldb-reconciliation')
        .then((res) => {
          this.loading = false;
          this.$store.commit('Loading_State', false);
          if (res.data.code == 200 || res.data.success) {
            const data = res.data.data;
            const list = Array.isArray(data) ? data : (data.data || []);
            list.sort((a, b) => new Date(b.date_from || b.created_at) - new Date(a.date_from || a.created_at));
            this.batches = list;
          }
        })
        .catch(() => {
          this.loading = false;
          this.$store.commit('Loading_State', false);
          this.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: 'Failed to load batches',
          });
        });
    },
    saveSettlement(batch) {
      const s = this.getSettlement(batch);
      this.saving = batch.batch_id;
      this.$axios
        .put(`ldb-reconciliation/${batch.batch_id}`, {
          actual_received_amount: s.actual_received_amount || null,
          comment: s.comment || null,
        })
        .then((res) => {
          this.saving = null;
          if (res.data.code == 200 || res.data.success) {
            this.$store.commit('Toast_State', {
              value: true,
              color: 'success',
              msg: 'Settlement saved',
            });
            this.fetchData();
          }
        })
        .catch(() => {
          this.saving = null;
          this.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: 'Failed to save settlement',
          });
        });
    },
    confirmClose(batch) {
      this.closingBatch = batch;
      this.showCloseDialog = true;
    },
    doClose() {
      const batch = this.closingBatch;
      this.closing = batch.batch_id;
      this.showCloseDialog = false;
      this.$axios
        .put(`ldb-reconciliation/${batch.batch_id}`, {
          status: 'closed',
        })
        .then((res) => {
          this.closing = null;
          if (res.data.code == 200 || res.data.success) {
            this.$store.commit('Toast_State', {
              value: true,
              color: 'success',
              msg: 'EOD batch closed',
            });
            this.fetchData();
          }
        })
        .catch(() => {
          this.closing = null;
          this.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: 'Failed to close batch',
          });
        });
    },
    confirmDelete(batch) {
      this.deletingBatch = batch;
      this.showDeleteDialog = true;
    },
    doDelete() {
      const batch = this.deletingBatch;
      this.deleting = batch.batch_id;
      this.showDeleteDialog = false;
      this.$axios
        .delete(`ldb-reconciliation/${batch.batch_id}`)
        .then((res) => {
          this.deleting = null;
          if (res.data.code == 200 || res.data.success) {
            this.$store.commit('Toast_State', {
              value: true,
              color: 'success',
              msg: 'Batch deleted',
            });
            this.fetchData();
          }
        })
        .catch(() => {
          this.deleting = null;
          this.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: 'Failed to delete batch',
          });
        });
    },
    runReconciliation() {
      this.runLoading = true;
      this.$axios
        .post('ldb-reconciliation/reconcile', {
          date_from: this.runForm.date_from,
          date_to: this.runForm.date_from,
        }, {
          timeout: 200000,
        })
        .then((res) => {
          this.runLoading = false;
          if (res.data.code == 200 || res.data.success) {
            this.showRunModal = false;
            const d = res.data.data;
            // The API returns summary.{status}.count; reading d.matched printed the whole object.
            const s = d.summary || {};
            const n = (key) => (s[key] && s[key].count) || 0;
            const summary = `Total: ${d.total_records || 0}`
              + `\nMatched: ${n('matched')}`
              + `\nMissed: ${n('missed_payment')}`
              + `\nAmount mismatch: ${n('amount_mismatch')}`
              + `\nExpired: ${n('expired')}`
              + `\nErrors: ${n('ldb_error')}`;
            // eslint-disable-next-line no-alert
            alert(`Reconciliation completed!\n\n${summary}`);
            const batchId = d.batch_id || d.id;
            if (batchId) {
              this.$router.push(`/eod/ldb-reconciliation/${batchId}`);
            } else {
              this.fetchData();
            }
          }
        })
        .catch((error) => {
          this.runLoading = false;
          const msg = error.response && error.response.data
            ? error.response.data.message
            : 'Reconciliation failed';
          if (error.response && error.response.status === 409) {
            // eslint-disable-next-line no-alert
            alert(msg);
          } else {
            // eslint-disable-next-line no-alert
            alert(`${this.t.reconciliationFailed}\n\n${msg}`);
          }
        });
    },
    checkPendingQr() {
      this.checkPendingLoading = true;
      this.$axios
        .post('ldb-reconciliation/check-pending-qr', {
          days: this.checkPendingDays,
        }, {
          timeout: 120000,
        })
        .then((res) => {
          this.checkPendingLoading = false;
          this.showCheckPendingModal = false;
          if (res.data.code == 200 || res.data.success) {
            const d = res.data.data;
            const completed = d.completed || 0;
            const stillPending = d.still_pending || 0;
            const msg = completed > 0
              ? this.t.checkPendingSuccess
                .replace('{completed}', completed)
                .replace('{still_pending}', stillPending)
              : this.t.checkPendingNone
                .replace('{still_pending}', stillPending);
            this.$store.commit('Toast_State', {
              value: true,
              color: completed > 0 ? 'success' : 'info',
              msg,
            });
            if (completed > 0) {
              this.fetchData();
            }
          }
        })
        .catch((error) => {
          this.checkPendingLoading = false;
          const msg = error.response && error.response.data
            ? error.response.data.message
            : 'Failed to check pending QR';
          this.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg,
          });
        });
    },
    viewBatch(batch) {
      const query = Number(batch.missed_payment_count) > 0
        ? { match_status: 'missed_payment' }
        : {};
      this.$router.push({
        path: `/eod/ldb-reconciliation/${batch.batch_id}`,
        query,
      });
    },
    openPermissions() {
      this.showPermDialog = true;
      this.fetchPermissions();
    },
    fetchPermissions() {
      this.$axios
        .get('permissions/eod-ldb-reconciliation')
        .then((res) => {
          if (res.data.code == 200 || res.data.success) {
            this.permData = res.data.data;
          }
        })
        .catch(() => {});
    },
    assignRole() {
      if (!this.selectedRole) return;
      this.assigningRole = true;
      this.$axios
        .post('permissions/eod-ldb-reconciliation/assign-role', { role: this.selectedRole })
        .then((res) => {
          this.assigningRole = false;
          if (res.data.code == 200 || res.data.success) {
            this.selectedRole = '';
            this.fetchPermissions();
          }
        })
        .catch(() => { this.assigningRole = false; });
    },
    revokeRole(roleName) {
      this.revokingRole = roleName;
      this.$axios
        .post('permissions/eod-ldb-reconciliation/revoke-role', { role: roleName })
        .then((res) => {
          this.revokingRole = null;
          if (res.data.code == 200 || res.data.success) {
            this.fetchPermissions();
          }
        })
        .catch(() => { this.revokingRole = null; });
    },
    searchAndAssignUser() {
      if (!this.addUserSearch) return;
      this.assigningUser = true;
      this.$axios
        .get('user', { params: { filter: this.addUserSearch, per_page: 1 } })
        .then((res) => {
          const users = res.data.data ? (res.data.data.data || res.data.data) : [];
          if (users.length === 0) {
            this.assigningUser = false;
            // eslint-disable-next-line no-alert
            alert('User not found');
            return;
          }
          const user = users[0];
          this.$axios
            .post('permissions/eod-ldb-reconciliation/assign-user', { user_id: user.id })
            .then((r2) => {
              this.assigningUser = false;
              if (r2.data.code == 200 || r2.data.success) {
                this.addUserSearch = '';
                this.fetchPermissions();
              }
            })
            .catch(() => { this.assigningUser = false; });
        })
        .catch(() => { this.assigningUser = false; });
    },
    revokeUser(userId) {
      this.revokingUser = userId;
      this.$axios
        .post('permissions/eod-ldb-reconciliation/revoke-user', { user_id: userId })
        .then((res) => {
          this.revokingUser = null;
          if (res.data.code == 200 || res.data.success) {
            this.fetchPermissions();
          }
        })
        .catch(() => { this.revokingUser = null; });
    },
    exportBatch(batchId) {
      this.$axios
        .get(`ldb-reconciliation/${batchId}/export`)
        .then((res) => {
          if ((res.data.code == 200 || res.data.success) && res.data.data.download_link) {
            window.open(res.data.data.download_link, '_blank');
          }
        })
        .catch(() => {
          this.$store.commit('Toast_State', {
            value: true,
            color: 'error',
            msg: 'Failed to export',
          });
        });
    },
  },
};
</script>

<style scoped>
.batch-card {
  border-radius: 8px !important;
  border-left: 4px solid #49A3DA;
}
.batch-header {
  background-color: #fafafa;
}
.theme--dark .batch-header {
  background-color: #2c2c2c;
}
</style>

<style>
.summary-table th {
  font-size: 14px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  color: #555 !important;
  background-color: #f5f5f5 !important;
}
.summary-table td {
  font-size: 15px !important;
  color: #222 !important;
  font-weight: 500 !important;
  padding: 8px 16px !important;
}
.summary-table .row-green td {
  background-color: #f0faf0;
}
.summary-table .row-red td {
  background-color: #fff5f5;
}
.summary-table .row-orange td {
  background-color: #fff8f0;
}
.summary-table .row-grey td {
  background-color: #f8f8f8;
}
.summary-table .row-amber td {
  background-color: #fffbf0;
}
.diff-zero input {
  color: green !important;
  font-weight: 700 !important;
}
.diff-nonzero input {
  color: red !important;
  font-weight: 700 !important;
}
.settlement-input input,
.settlement-input textarea {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #222 !important;
}
.settlement-input .v-input__append-inner,
.settlement-input .v-text-field__suffix {
  font-size: 14px !important;
  font-weight: 600 !important;
}

/* Dark mode overrides */
.theme--dark .summary-table th {
  color: #ccc !important;
  background-color: #2c2c2c !important;
}
.theme--dark .summary-table td {
  color: #e0e0e0 !important;
}
.theme--dark .summary-table .row-green td {
  background-color: rgba(76, 175, 80, 0.15);
}
.theme--dark .summary-table .row-red td {
  background-color: rgba(244, 67, 54, 0.15);
}
.theme--dark .summary-table .row-orange td {
  background-color: rgba(255, 152, 0, 0.15);
}
.theme--dark .summary-table .row-grey td {
  background-color: #2a2a2a;
}
.theme--dark .summary-table .row-amber td {
  background-color: rgba(255, 193, 7, 0.15);
}
.theme--dark .settlement-input input,
.theme--dark .settlement-input textarea {
  color: #e0e0e0 !important;
}
</style>
